// src/features/auth/authSlice.js
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { authAPI, profileAPI } from "../../services/api";
import { registerDeviceToken, unregisterDeviceToken, getDeviceId } from '../../firebase/firebase';

const getInitialState = () => {
  const token = localStorage.getItem("token");
  const user = localStorage.getItem("user");
  const isAuthenticated = localStorage.getItem("isAuthenticated") === "true";
  const profileStatusStr = localStorage.getItem("profileStatus");

  let profileStatus = {
    basicInfoCompleted: false,
    documentsCompleted: false,
    bankDetailsCompleted: false,
    profileStep: "basic_info",
    verificationStatus: "pending",
    verificationMessage: "",
    isVerified: false,
  };

  if (profileStatusStr) {
    try {
      profileStatus = JSON.parse(profileStatusStr);
    } catch (e) {}
  }

  return {
    user: user ? JSON.parse(user) : null,
    token: token || null,
    isAuthenticated: isAuthenticated,
    isLoading: false,
    error: null,
    otpSent: false,
    verificationEmail: null,
    passwordSet: false,
    basicInfoCompleted: profileStatus.basicInfoCompleted,
    documentsCompleted: profileStatus.documentsCompleted,
    bankDetailsCompleted: profileStatus.bankDetailsCompleted,
    profileStep: profileStatus.profileStep,
    verificationStatus: profileStatus.verificationStatus,
    verificationMessage: profileStatus.verificationMessage,
    isVerified: profileStatus.isVerified,
    uploadedDocuments: [],
    documentTypes: [],
  };
};

export const signupUser = createAsyncThunk(
  "auth/signup",
  async (email, { rejectWithValue }) => {
    try {
      const response = await authAPI.signup({ Email: email });

      if (response.data.otp || response.data.Otp || response.data.OTP) {
        const otp = response.data.otp || response.data.Otp || response.data.OTP;
        alert(`Your OTP is: ${otp}`);
      }

      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message ||
          error.response?.data?.Message ||
          "Signup failed. Please try again."
      );
    }
  }
);

export const verifyOtp = createAsyncThunk(
  "auth/verifyOtp",
  async ({ email, otp }, { rejectWithValue }) => {
    try {
      const response = await authAPI.verifyOtp({ Email: email, Otp: otp });

      let token =
        response.data.Token ||
        response.data.token ||
        response.data.access ||
        response.data.access_token ||
        response.data.data?.Token ||
        response.data.data?.token;

      if (token) {
        localStorage.setItem("token", token);
        localStorage.setItem("isAuthenticated", "false");
      }

      let user =
        response.data.User ||
        response.data.user ||
        response.data.data?.User ||
        response.data.data?.user;
      if (user) {
        localStorage.setItem("user", JSON.stringify(user));
      }

      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message ||
          error.response?.data?.Message ||
          error.response?.data?.Error ||
          "OTP verification failed. Please try again."
      );
    }
  }
);

export const resendOtp = createAsyncThunk(
  "auth/resendOtp",
  async (email, { rejectWithValue }) => {
    try {
      const response = await authAPI.resendOtp({ Email: email });

      if (response.data.otp || response.data.Otp || response.data.OTP) {
        const otp = response.data.otp || response.data.Otp || response.data.OTP;
        alert(`Your new OTP is: ${otp}`);
      }

      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message ||
          error.response?.data?.Message ||
          "Failed to resend OTP. Please try again."
      );
    }
  }
);

export const setPassword = createAsyncThunk(
  "auth/setPassword",
  async ({ password }, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        return rejectWithValue(
          "Authentication token not found. Please verify OTP again."
        );
      }

      const response = await authAPI.setPassword({
        Password: password,
        ConfirmPassword: password,
      });

      if (response.data.Token || response.data.token) {
        const token = response.data.Token || response.data.token;
        localStorage.setItem("token", token);
        localStorage.setItem("isAuthenticated", "true");
      }

      if (response.data.User || response.data.user) {
        const user = response.data.User || response.data.user;
        localStorage.setItem("user", JSON.stringify(user));
      }

      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.Error ||
          error.response?.data?.error ||
          error.response?.data?.message ||
          error.response?.data?.Message ||
          "Failed to set password. Please try again."
      );
    }
  }
);

// Login user with FCM token registration
export const loginUser = createAsyncThunk(
  "auth/login",
  async ({ email, password }, { rejectWithValue }) => {
    try {
      const fcmToken = localStorage.getItem("fcmToken") || "";

      const response = await authAPI.login({
        Email: email,
        Password: password,
        FcmToken: fcmToken,
        DeviceType: "WEB",
        DeviceId: getDeviceId(),
        DeviceName: navigator.userAgent.substring(0, 50),
      });

      const token = response.data.Token || response.data.token;
      const user = response.data.User || response.data.user;

      if (token) {
        localStorage.setItem("token", token);
        localStorage.setItem("isAuthenticated", "true");
      }

      if (user) {
        localStorage.setItem("user", JSON.stringify(user));
      }

      // Register device token after login success
      setTimeout(() => {
        registerDeviceToken().catch(() => {});
      }, 1000);

      return response.data;
    } catch (error) {
      if (error.response?.status === 401) {
        return rejectWithValue(
          error.response?.data?.Error ||
            error.response?.data?.message ||
            "Invalid email or password. Please check your credentials."
        );
      }
      return rejectWithValue(
        error.response?.data?.Error ||
          error.response?.data?.message ||
          "Login failed. Please try again."
      );
    }
  }
);

// Google login with FCM token registration
export const googleLogin = createAsyncThunk(
  "auth/googleLogin",
  async (credential, { rejectWithValue }) => {
    try {
      const fcmToken = localStorage.getItem("fcmToken") || "";

      const response = await authAPI.googleLogin({
        IdToken: credential,
        FcmToken: fcmToken,
        DeviceType: "WEB",
        DeviceId: getDeviceId(),
        DeviceName: navigator.userAgent.substring(0, 50),
      });

      const token = response.data.Token || response.data.token;
      const user = response.data.User || response.data.user;

      if (token) {
        localStorage.setItem("token", token);
        localStorage.setItem("isAuthenticated", "true");
      }

      if (user) {
        localStorage.setItem("user", JSON.stringify(user));
      }

      const profile = response.data.Profile || response.data.profile;
      if (profile) {
        const verificationStatus =
          profile?.VerificationStatus ??
          profile?.verification_status ??
          "pending";
        const isProfileVerified =
          profile?.IsVerified ?? profile?.is_verified ?? false;

        localStorage.setItem(
          "profileStatus",
          JSON.stringify({
            basicInfoCompleted:
              profile?.BasicInfoCompleted ??
              profile?.basic_info_completed ??
              false,
            documentsCompleted:
              profile?.Documents ?? profile?.documents_completed ?? false,
            bankDetailsCompleted: profile?.bank_details_completed ?? false,
            profileStep:
              profile?.ProfileStep ?? profile?.profile_step ?? "basic_info",
            verificationStatus: verificationStatus,
            verificationMessage:
              profile?.VerificationMessage ??
              profile?.verification_message ??
              "",
            isVerified:
              isProfileVerified === true ||
              verificationStatus === "verified" ||
              verificationStatus === "approved",
          })
        );
      }

      // Register device token after Google login success
      setTimeout(() => {
        registerDeviceToken().catch(() => {});
      }, 1000);

      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.Error ||
          error.response?.data?.message ||
          "Google login failed. Please try again."
      );
    }
  }
);

// Apple login with FCM token registration
export const appleLogin = createAsyncThunk(
  "auth/appleLogin",
  async ({ idToken, authorizationCode, user }, { rejectWithValue }) => {
    try {
      const fcmToken = localStorage.getItem("fcmToken") || "";

      // Extract user info if available (only comes on first Apple login)
      const fullName = user?.name ? `${user.name.firstName || ''} ${user.name.lastName || ''}`.trim() : '';
      const email = user?.email || '';

      const response = await authAPI.appleLogin({
        IdentityToken: idToken,
        AuthorizationCode: authorizationCode,
        FullName: fullName,
        Email: email,
        FcmToken: fcmToken,
        DeviceType: "WEB",
        DeviceId: getDeviceId(),
        DeviceName: navigator.userAgent.substring(0, 50),
      });

      const token = response.data.Token || response.data.token;
      const userData = response.data.User || response.data.user;

      if (token) {
        localStorage.setItem("token", token);
        localStorage.setItem("isAuthenticated", "true");
      }

      if (userData) {
        localStorage.setItem("user", JSON.stringify(userData));
      }

      const profile = response.data.Profile || response.data.profile;
      if (profile) {
        const verificationStatus =
          profile?.VerificationStatus ??
          profile?.verification_status ??
          "pending";
        const isProfileVerified =
          profile?.IsVerified ?? profile?.is_verified ?? false;

        localStorage.setItem(
          "profileStatus",
          JSON.stringify({
            basicInfoCompleted:
              profile?.BasicInfoCompleted ??
              profile?.basic_info_completed ??
              false,
            documentsCompleted:
              profile?.Documents ?? profile?.documents_completed ?? false,
            bankDetailsCompleted: profile?.bank_details_completed ?? false,
            profileStep:
              profile?.ProfileStep ?? profile?.profile_step ?? "basic_info",
            verificationStatus: verificationStatus,
            verificationMessage:
              profile?.VerificationMessage ??
              profile?.verification_message ??
              "",
            isVerified:
              isProfileVerified === true ||
              verificationStatus === "verified" ||
              verificationStatus === "approved",
          })
        );
      }

      // Register device token after Apple login success
      setTimeout(() => {
        registerDeviceToken().catch(() => {});
      }, 1000);

      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.Error ||
          error.response?.data?.message ||
          "Apple login failed. Please try again."
      );
    }
  }
);

// Logout user with device token cleanup
export const logoutUser = createAsyncThunk(
  "auth/logoutUser",
  async (_, { rejectWithValue }) => {
    try {
      // Unregister device token from backend
      await unregisterDeviceToken();
      return { success: true };
    } catch (error) {
      // Still proceed with local logout even if API fails
      return { success: true };
    }
  }
);

export const sendForgotPasswordOtp = createAsyncThunk(
  "auth/sendForgotPasswordOtp",
  async (email, { rejectWithValue }) => {
    try {
      const response = await authAPI.sendForgotPasswordOtp({ Email: email });

      if (response.data.otp || response.data.Otp || response.data.OTP) {
        const otp = response.data.otp || response.data.Otp || response.data.OTP;
        alert(`Your Reset OTP is: ${otp}`);
      }

      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message ||
          error.response?.data?.Message ||
          "Failed to send OTP. Please try again."
      );
    }
  }
);

export const verifyForgotPasswordOtp = createAsyncThunk(
  "auth/verifyForgotPasswordOtp",
  async ({ email, otp }, { rejectWithValue }) => {
    try {
      const response = await authAPI.verifyForgotPasswordOtp({
        Email: email,
        Otp: otp,
      });

      let token =
        response.data.Token ||
        response.data.token ||
        response.data.access ||
        response.data.access_token;

      if (token) {
        localStorage.setItem("token", token);
      }

      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message ||
          error.response?.data?.Message ||
          error.response?.data?.Error ||
          "Invalid OTP. Please try again."
      );
    }
  }
);

export const resetPassword = createAsyncThunk(
  "auth/resetPassword",
  async ({ email, password }, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        return rejectWithValue(
          "Authentication token not found. Please verify OTP again."
        );
      }

      const response = await authAPI.resetPassword({
        Password: password,
        ConfirmPassword: password,
      });

      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.Error ||
          error.response?.data?.error ||
          error.response?.data?.message ||
          error.response?.data?.Message ||
          "Failed to reset password. Please try again."
      );
    }
  }
);

export const submitBasicInfo = createAsyncThunk(
  "auth/submitBasicInfo",
  async (formData, { rejectWithValue }) => {
    try {
      const response = await authAPI.submitBasicInfo(formData);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.Error ||
          error.response?.data?.message ||
          error.response?.data?.Message ||
          "Failed to submit basic info. Please try again."
      );
    }
  }
);

export const getProfile = createAsyncThunk(
  "auth/getProfile",
  async (_, { rejectWithValue }) => {
    try {
      const response = await profileAPI.getProfile();
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.Error ||
          error.response?.data?.message ||
          "Failed to fetch profile."
      );
    }
  }
);

export const getDocumentTypes = createAsyncThunk(
  "auth/getDocumentTypes",
  async (_, { rejectWithValue }) => {
    try {
      const response = await authAPI.getDocumentTypes();
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.Error ||
          error.response?.data?.message ||
          "Failed to fetch document types."
      );
    }
  }
);

export const uploadDocument = createAsyncThunk(
  "auth/uploadDocument",
  async (formData, { rejectWithValue }) => {
    try {
      const response = await authAPI.uploadDocument(formData);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.Error ||
          error.response?.data?.message ||
          error.response?.data?.Message ||
          "Failed to upload document. Please try again."
      );
    }
  }
);

export const uploadMultipleDocuments = createAsyncThunk(
  "auth/uploadMultipleDocuments",
  async (formData, { rejectWithValue }) => {
    try {
      const response = await authAPI.uploadMultipleDocuments(formData);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.Error ||
          error.response?.data?.message ||
          error.response?.data?.Message ||
          "Failed to upload documents. Please try again."
      );
    }
  }
);

const updateProfileStatusInLocalStorage = (state) => {
  const profileStatus = {
    basicInfoCompleted: state.basicInfoCompleted,
    documentsCompleted: state.documentsCompleted,
    bankDetailsCompleted: state.bankDetailsCompleted,
    profileStep: state.profileStep,
    verificationStatus: state.verificationStatus,
    verificationMessage: state.verificationMessage,
    isVerified: state.isVerified,
  };
  localStorage.setItem("profileStatus", JSON.stringify(profileStatus));
  return profileStatus;
};

// Helper function to clear all auth data
const clearAuthData = () => {
  localStorage.removeItem("token");
  localStorage.removeItem("user");
  localStorage.removeItem("isAuthenticated");
  localStorage.removeItem("profileStatus");
  localStorage.removeItem("businessDetails");
  localStorage.removeItem("userEmail");
  // Note: We keep deviceId and fcmToken for device tracking
};

const authSlice = createSlice({
  name: "auth",
  initialState: getInitialState(),
  reducers: {
    // Synchronous logout (use logoutUser thunk for async with API call)
    logout: (state) => {
      state.user = null;
      state.token = null;
      state.isAuthenticated = false;
      state.isVerified = false;
      state.basicInfoCompleted = false;
      state.documentsCompleted = false;
      state.bankDetailsCompleted = false;
      state.profileStep = "basic_info";
      state.verificationStatus = "pending";
      state.verificationMessage = "";
      state.error = null;
      state.otpSent = false;
      state.verificationEmail = null;
      state.passwordSet = false;

      clearAuthData();
    },
    clearError: (state) => {
      state.error = null;
    },
    setVerificationEmail: (state, action) => {
      state.verificationEmail = action.payload;
    },
    resetOtpState: (state) => {
      state.otpSent = false;
      state.verificationEmail = null;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(signupUser.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(signupUser.fulfilled, (state) => {
        state.isLoading = false;
        state.otpSent = true;
        state.error = null;
      })
      .addCase(signupUser.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
        state.otpSent = false;
      });

    builder
      .addCase(verifyOtp.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(verifyOtp.fulfilled, (state, action) => {
        state.isLoading = false;
        state.token =
          action.payload.Token || action.payload.token || action.payload.access;
        state.user = action.payload.User || action.payload.user;
        state.isAuthenticated = false;
        state.error = null;
        state.otpSent = false;
      })
      .addCase(verifyOtp.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });

    builder
      .addCase(resendOtp.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(resendOtp.fulfilled, (state) => {
        state.isLoading = false;
        state.error = null;
      })
      .addCase(resendOtp.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });

    builder
      .addCase(setPassword.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(setPassword.fulfilled, (state, action) => {
        state.isLoading = false;
        state.passwordSet = true;

        const token = action.payload.Token || action.payload.token;
        if (token) {
          state.token = token;
          state.isAuthenticated = true;
        }

        const user = action.payload.User || action.payload.user;
        if (user) {
          state.user = user;
        }

        state.error = null;
      })
      .addCase(setPassword.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
        state.passwordSet = false;
      });

    builder
      .addCase(loginUser.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.isLoading = false;
        state.token = action.payload.Token || action.payload.token;
        state.user = action.payload.User || action.payload.user;
        state.isAuthenticated = !!state.token;
        state.error = null;

        const profile = action.payload.Profile || action.payload.profile;

        state.basicInfoCompleted =
          profile?.BasicInfoCompleted ?? profile?.basic_info_completed ?? false;
        state.documentsCompleted =
          profile?.Documents ?? profile?.documents_completed ?? false;
        state.bankDetailsCompleted = profile?.bank_details_completed ?? false;
        state.profileStep =
          profile?.ProfileStep ?? profile?.profile_step ?? "basic_info";

        const verificationStatus =
          profile?.VerificationStatus ??
          profile?.verification_status ??
          "pending";
        const isProfileVerified =
          profile?.IsVerified ?? profile?.is_verified ?? false;

        state.verificationStatus = verificationStatus;
        state.verificationMessage =
          profile?.VerificationMessage ?? profile?.verification_message ?? "";

        state.isVerified =
          isProfileVerified === true ||
          verificationStatus === "verified" ||
          verificationStatus === "approved";

        updateProfileStatusInLocalStorage(state);
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });

    builder
      .addCase(googleLogin.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(googleLogin.fulfilled, (state, action) => {
        state.isLoading = false;
        state.token = action.payload.Token || action.payload.token;
        state.user = action.payload.User || action.payload.user;
        state.isAuthenticated = !!state.token;
        state.error = null;

        const profile = action.payload.Profile || action.payload.profile;

        state.basicInfoCompleted =
          profile?.BasicInfoCompleted ?? profile?.basic_info_completed ?? false;
        state.documentsCompleted =
          profile?.Documents ?? profile?.documents_completed ?? false;
        state.bankDetailsCompleted = profile?.bank_details_completed ?? false;
        state.profileStep =
          profile?.ProfileStep ?? profile?.profile_step ?? "basic_info";

        const verificationStatus =
          profile?.VerificationStatus ??
          profile?.verification_status ??
          "pending";
        const isProfileVerified =
          profile?.IsVerified ?? profile?.is_verified ?? false;

        state.verificationStatus = verificationStatus;
        state.verificationMessage =
          profile?.VerificationMessage ?? profile?.verification_message ?? "";

        state.isVerified =
          isProfileVerified === true ||
          verificationStatus === "verified" ||
          verificationStatus === "approved";

        updateProfileStatusInLocalStorage(state);
      })
      .addCase(googleLogin.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });

    builder
      .addCase(appleLogin.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(appleLogin.fulfilled, (state, action) => {
        state.isLoading = false;
        state.token = action.payload.Token || action.payload.token;
        state.user = action.payload.User || action.payload.user;
        state.isAuthenticated = !!state.token;
        state.error = null;

        const profile = action.payload.Profile || action.payload.profile;

        state.basicInfoCompleted =
          profile?.BasicInfoCompleted ?? profile?.basic_info_completed ?? false;
        state.documentsCompleted =
          profile?.Documents ?? profile?.documents_completed ?? false;
        state.bankDetailsCompleted = profile?.bank_details_completed ?? false;
        state.profileStep =
          profile?.ProfileStep ?? profile?.profile_step ?? "basic_info";

        const verificationStatus =
          profile?.VerificationStatus ??
          profile?.verification_status ??
          "pending";
        const isProfileVerified =
          profile?.IsVerified ?? profile?.is_verified ?? false;

        state.verificationStatus = verificationStatus;
        state.verificationMessage =
          profile?.VerificationMessage ?? profile?.verification_message ?? "";

        state.isVerified =
          isProfileVerified === true ||
          verificationStatus === "verified" ||
          verificationStatus === "approved";

        updateProfileStatusInLocalStorage(state);
      })
      .addCase(appleLogin.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });

    // Logout user thunk
    builder
      .addCase(logoutUser.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(logoutUser.fulfilled, (state) => {
        state.isLoading = false;
        state.user = null;
        state.token = null;
        state.isAuthenticated = false;
        state.isVerified = false;
        state.basicInfoCompleted = false;
        state.documentsCompleted = false;
        state.bankDetailsCompleted = false;
        state.profileStep = "basic_info";
        state.verificationStatus = "pending";
        state.verificationMessage = "";
        state.error = null;
        state.otpSent = false;
        state.verificationEmail = null;
        state.passwordSet = false;

        clearAuthData();
      })
      .addCase(logoutUser.rejected, (state) => {
        // Still logout locally even if API fails
        state.isLoading = false;
        state.user = null;
        state.token = null;
        state.isAuthenticated = false;
        state.isVerified = false;
        state.basicInfoCompleted = false;
        state.documentsCompleted = false;
        state.bankDetailsCompleted = false;
        state.profileStep = "basic_info";
        state.verificationStatus = "pending";
        state.verificationMessage = "";
        state.error = null;
        state.otpSent = false;
        state.verificationEmail = null;
        state.passwordSet = false;

        clearAuthData();
      });

    builder
      .addCase(sendForgotPasswordOtp.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(sendForgotPasswordOtp.fulfilled, (state) => {
        state.isLoading = false;
        state.otpSent = true;
        state.error = null;
      })
      .addCase(sendForgotPasswordOtp.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });

    builder
      .addCase(verifyForgotPasswordOtp.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(verifyForgotPasswordOtp.fulfilled, (state, action) => {
        state.isLoading = false;
        const token = action.payload.Token || action.payload.token;
        if (token) {
          state.token = token;
        }
        state.error = null;
      })
      .addCase(verifyForgotPasswordOtp.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });

    builder
      .addCase(resetPassword.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(resetPassword.fulfilled, (state) => {
        state.isLoading = false;
        state.error = null;
        state.verificationEmail = null;
        state.otpSent = false;
        state.token = null;
        localStorage.removeItem("token");
      })
      .addCase(resetPassword.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });

    builder
      .addCase(submitBasicInfo.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(submitBasicInfo.fulfilled, (state) => {
        state.isLoading = false;
        state.basicInfoCompleted = true;
        state.profileStep = "documents";
        state.error = null;
        updateProfileStatusInLocalStorage(state);
      })
      .addCase(submitBasicInfo.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });

    builder
      .addCase(uploadDocument.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(uploadDocument.fulfilled, (state, action) => {
        state.isLoading = false;
        if (action.payload.Document) {
          state.uploadedDocuments.push(action.payload.Document);
        }
        state.error = null;
      })
      .addCase(uploadDocument.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });

    builder
      .addCase(getProfile.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getProfile.fulfilled, (state, action) => {
        state.isLoading = false;

        const profile =
          action.payload.Profile || action.payload.profile || action.payload;
        const user = action.payload.User || action.payload.user;

        const hasBusinessInfo = !!(
          profile?.BusinessName &&
          profile?.BusinessAddress &&
          profile?.BusinessPhone
        );

        if (hasBusinessInfo) {
          const businessDetails = {
            BusinessName: profile.BusinessName,
            BusinessAddress: profile.BusinessAddress,
            BusinessPhone: profile.BusinessPhone,
            BusinessEmail: profile.BusinessEmail,
            BusinessLogo: profile.BusinessLogo,
          };
          localStorage.setItem(
            "businessDetails",
            JSON.stringify(businessDetails)
          );
        }

        state.basicInfoCompleted =
          profile?.BasicInfoCompleted ??
          profile?.basic_info_completed ??
          hasBusinessInfo;

        const documentsValue =
          profile?.Documents ??
          profile?.documents_completed ??
          profile?.DocumentsCompleted;
        const hasDocumentsFromAPI =
          documentsValue === true ||
          (typeof documentsValue === "number" && documentsValue > 0) ||
          (Array.isArray(documentsValue) && documentsValue.length > 0);

        const storedProfileStatus = localStorage.getItem("profileStatus");
        let storedDocumentsCompleted = false;
        if (storedProfileStatus) {
          try {
            const parsed = JSON.parse(storedProfileStatus);
            storedDocumentsCompleted = parsed.documentsCompleted === true;
          } catch (e) {}
        }

        state.documentsCompleted =
          hasDocumentsFromAPI || storedDocumentsCompleted;
        state.bankDetailsCompleted = profile?.bank_details_completed ?? false;

        if (hasBusinessInfo && !state.documentsCompleted) {
          state.profileStep = "documents";
        } else if (hasBusinessInfo && state.documentsCompleted) {
          state.profileStep = "verification_pending";
        } else {
          state.profileStep =
            profile?.ProfileStep ?? profile?.profile_step ?? "basic_info";
        }

        const verificationStatus =
          profile?.VerificationStatus ??
          profile?.verification_status ??
          "pending";
        const isProfileVerified =
          profile?.IsVerified ?? profile?.is_verified ?? false;

        state.verificationStatus = verificationStatus;
        state.verificationMessage =
          profile?.VerificationMessage ?? profile?.verification_message ?? "";

        state.isVerified =
          isProfileVerified === true ||
          verificationStatus === "verified" ||
          verificationStatus === "approved";

        if (user) {
          state.user = user;
        }

        updateProfileStatusInLocalStorage(state);
        state.error = null;
      })
      .addCase(getProfile.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });

    builder
      .addCase(getDocumentTypes.pending, (state) => {
        state.error = null;
      })
      .addCase(getDocumentTypes.fulfilled, (state, action) => {
        state.documentTypes =
          action.payload.DocumentTypes || action.payload.Data || [];
        state.error = null;
      })
      .addCase(getDocumentTypes.rejected, (state, action) => {
        state.error = action.payload;
      });

    builder
      .addCase(uploadMultipleDocuments.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(uploadMultipleDocuments.fulfilled, (state, action) => {
        state.isLoading = false;
        state.documentsCompleted = true;
        state.profileStep = "verification_pending";

        if (action.payload.Documents) {
          state.uploadedDocuments = action.payload.Documents;
        }

        updateProfileStatusInLocalStorage(state);
        state.error = null;
      })
      .addCase(uploadMultipleDocuments.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });
  },
});

export const { logout, clearError, setVerificationEmail, resetOtpState } =
  authSlice.actions;
export default authSlice.reducer;